import React from 'react'
import useTranslation from 'next-translate/useTranslation'
import Link from 'next/link'
import {
  ChevronRightIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/24/outline'
import Button from 'components/atoms/Button'
import { Company } from 'generated/apolloComponents'
import styles from './styles.module.scss'
import { arrayOfObjectToStringArrayByKey } from '@/helpers/utils'
import DropdownString from '../DropdownString'

interface iProps {
  company: Company
  index?: number
}

const CompanyListItem = ({ company, index }: iProps) => {
  const { t, lang } = useTranslation('common')
  const loading = index && index > 3 ? 'lazy' : 'eager'
  // naming due to https://github.com/aralroca/next-translate#5-plurals
  const count = company.locations.length

  return (
    <div key={company.id}>
      <a
        href={`/companies/profiles/${company.id}`}
        className={`relative flex flex-col md:flex-row bg-white py-5 md:py-4 px-5 md:px-4 rounded-lg shadow
        ${company.verified ? styles.cli__isVerified : ''}
        ${styles.cli}
      `}
      >
        <div className={`${styles.cli__wrapper} relative flex pb-3 md:pb-0`}>
          {company.logo?.url ? (
            <div className='relative'>
              <div className={`${styles.cli__logo} flex relative`}>
                <div className='w-full flex m-auto'>
                  <img
                    src={company.logo?.url}
                    alt={company.name}
                    loading={loading}
                  />
                </div>
              </div>
              <div className={`${styles.cli__logo__divider} absolute`} />
            </div>
          ) : (
            <div className='relative'>
              <div className={`${styles.cli__logo} flex relative`}>
                <div className='w-full flex m-auto text-gray-100'>
                  <BuildingOffice2Icon className='w-3/4' />
                </div>
              </div>
              <div className={`${styles.cli__logo__divider} absolute`} />
            </div>
          )}

          <div
            className={`${styles.cli__data} md:pr-2 flex flex-col justify-between`}
          >
            <div className='flex items-center justify-start'>
              <h3
                className={`${styles.cli__companyName} text-18 font-bold md:w-auto`}
              >
                {company.name}
              </h3>
            </div>
            <div>
              <div className='flex flex-wrap justify-start gap-3'>
                {company.skills?.slice(0, 5).map((technology) => (
                  <span className='py-2 px-3 bg-neutral-200 rounded-full font-medium text-xs text-neutral-700'>
                    {technology}
                  </span>
                ))}
              </div>
            </div>
            {company.locations.length > 0 && (
              <div>
                <DropdownString
                  icon
                  limit={2}
                  textSize='sm'
                  list={arrayOfObjectToStringArrayByKey(
                    company.locations,
                    lang === 'pl' ? 'cityPl' : 'cityEn'
                  )}
                />
              </div>
            )}
          </div>
        </div>
        <div className='flex md:ml-auto mt-5 md:mt-0'>
          <div
            className={`${styles.cli__buttons} flex items-center m-auto w-full md:w-auto`}
          >
            {!!company.activeOffersCount && (
              <Button
                href={`/companies/profiles/${company.id}#offers`}
                type='primary'
                size='large'
                className='w-1/2 md:w-auto mr-1.5 md:mr-0 font-medium text-md flex justify-center h-full'
              >
                {`${t('common:job_offers')} (${company.activeOffersCount})`}
              </Button>
            )}
            <Button
              href={`/companies/profiles/${company.id}`}
              type='outlined'
              size='large'
              className='w-1/2 md:w-auto mr-1.5 md:mr-0 font-medium text-md flex justify-center h-full md:hidden'
            >
              {`${t('common:show_profile')}`}
            </Button>
          </div>
        </div>
        <div className={`${styles.cli__chevron} hidden md:flex items-center`}>
          <ChevronRightIcon
            style={{ width: '2rem' }}
            className='mr-2 text-purple text-sm'
          />
        </div>
      </a>
    </div>
  )
}

export default CompanyListItem
